import React, { useEffect, useState, useCallback } from 'react';
import { Table, Badge, Button, Modal, Form, Tabs, Tab } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import api from '../services/api';

const SEVERITY_CFG = {
  minor:    { label: 'Mineure',  bg: 'secondary' },
  moderate: { label: 'Modérée',  bg: 'warning' },
  serious:  { label: 'Grave',    bg: 'danger' },
  critical: { label: 'Critique', bg: 'dark' },
};

const STATUS_CFG = {
  pending:    { label: 'En attente',   bg: 'warning' },
  confirmed:  { label: 'Confirmée',    bg: 'info' },
  sanctioned: { label: 'Sanctionnée',  bg: 'danger' },
  contested:  { label: 'Contestée',    bg: 'secondary' },
  dismissed:  { label: 'Classée',      bg: 'dark' },
};

const startIcon = new L.DivIcon({
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#00e676;border:2px solid #fff;box-shadow:0 0 6px #00e676"></div>',
  className: '', iconSize: [14, 14], iconAnchor: [7, 7],
});
const endIcon = new L.DivIcon({
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#ff1744;border:2px solid #fff;box-shadow:0 0 6px #ff1744"></div>',
  className: '', iconSize: [14, 14], iconAnchor: [7, 7],
});

// Map click handler for picking coordinates
function MapClickHandler({ onPick }) {
  useMapEvents({ click: (e) => onPick(e.latlng) });
  return null;
}

export default function Infractions() {
  const [tab, setTab] = useState('infractions');
  const [types, setTypes] = useState([]);
  const [rules, setRules] = useState([]);
  const [infractions, setInfractions] = useState([]);

  // Type form
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editType, setEditType] = useState(null);
  const [typeForm, setTypeForm] = useState({ name: '', code: '', description: '', severity: 'moderate', fine_amount: 0, points_penalty: 0 });

  // Rule form
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: '', infraction_type: '', start_lat: '', start_lng: '', end_lat: '', end_lng: '',
    corridor_m: 50, speed_limit: '', active_start: '', active_end: '', active_days: [],
  });
  const [pickingPoint, setPickingPoint] = useState(null); // 'start' or 'end'

  const loadTypes = useCallback(() => {
    api.get('/infraction-types/').then((r) => setTypes(r.data.results || r.data)).catch(() => {});
  }, []);

  const loadRules = useCallback(() => {
    api.get('/infraction-rules/').then((r) => setRules(r.data.results || r.data)).catch(() => {});
  }, []);

  const loadInfractions = useCallback(() => {
    api.get('/infractions/').then((r) => setInfractions(r.data.results || r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadTypes(); loadRules(); loadInfractions(); }, [loadTypes, loadRules, loadInfractions]);

  // ──── TYPE CRUD ────
  const openTypeModal = (type = null) => {
    if (type) {
      setEditType(type);
      setTypeForm({ name: type.name, code: type.code, description: type.description || '', severity: type.severity, fine_amount: type.fine_amount, points_penalty: type.points_penalty });
    } else {
      setEditType(null);
      setTypeForm({ name: '', code: '', description: '', severity: 'moderate', fine_amount: 0, points_penalty: 0 });
    }
    setShowTypeModal(true);
  };

  const saveType = () => {
    const req = editType
      ? api.put(`/infraction-types/${editType.id}/`, typeForm)
      : api.post('/infraction-types/', typeForm);
    req.then(() => { loadTypes(); setShowTypeModal(false); }).catch((e) => alert(e.response?.data ? JSON.stringify(e.response.data) : 'Erreur'));
  };

  const deleteType = (id) => {
    if (!window.confirm('Supprimer ce type d\'infraction ?')) return;
    api.delete(`/infraction-types/${id}/`).then(loadTypes).catch(() => {});
  };

  // ──── RULE CRUD ────
  const openRuleModal = (rule = null) => {
    if (rule) {
      setEditRule(rule);
      setRuleForm({
        name: rule.name, infraction_type: rule.infraction_type,
        start_lat: rule.start_lat, start_lng: rule.start_lng,
        end_lat: rule.end_lat, end_lng: rule.end_lng,
        corridor_m: rule.corridor_m, speed_limit: rule.speed_limit || '',
        active_start: rule.active_start || '', active_end: rule.active_end || '',
        active_days: rule.active_days || [],
      });
    } else {
      setEditRule(null);
      setRuleForm({
        name: '', infraction_type: types[0]?.id || '', start_lat: '', start_lng: '',
        end_lat: '', end_lng: '', corridor_m: 50, speed_limit: '',
        active_start: '', active_end: '', active_days: [],
      });
    }
    setPickingPoint(null);
    setShowRuleModal(true);
  };

  const saveRule = () => {
    const payload = {
      ...ruleForm,
      speed_limit: ruleForm.speed_limit || null,
      active_start: ruleForm.active_start || null,
      active_end: ruleForm.active_end || null,
      active_days: ruleForm.active_days.length > 0 ? ruleForm.active_days : [],
    };
    const req = editRule
      ? api.put(`/infraction-rules/${editRule.id}/`, payload)
      : api.post('/infraction-rules/', payload);
    req.then(() => { loadRules(); setShowRuleModal(false); }).catch((e) => alert(e.response?.data ? JSON.stringify(e.response.data) : 'Erreur'));
  };

  const deleteRule = (id) => {
    if (!window.confirm('Supprimer cette règle ?')) return;
    api.delete(`/infraction-rules/${id}/`).then(loadRules).catch(() => {});
  };

  const handleMapPick = (latlng) => {
    if (!pickingPoint) return;
    const key = pickingPoint === 'start' ? ['start_lat', 'start_lng'] : ['end_lat', 'end_lng'];
    setRuleForm((f) => ({ ...f, [key[0]]: latlng.lat.toFixed(6), [key[1]]: latlng.lng.toFixed(6) }));
    setPickingPoint(null);
  };

  // ──── INFRACTION ACTIONS ────
  const handleInfractionAction = (id, action, notes = '') => {
    const payload = action === 'dismiss' && notes ? { notes } : {};
    api.post(`/infractions/${id}/${action}/`, payload).then(loadInfractions).catch(() => {});
  };

  const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div style={{ padding: 20 }}>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 20 }}>
        <i className="bi bi-shield-slash" style={{ color: '#ff9100' }}></i> Gestion des Infractions
      </h4>

      <Tabs activeKey={tab} onSelect={setTab} className="mb-3" variant="pills"
        style={{ '--bs-nav-pills-link-active-bg': '#ff9100' }}>

        {/* ═══════ TAB: INFRACTIONS ═══════ */}
        <Tab eventKey="infractions" title={<><i className="bi bi-exclamation-octagon me-1"></i>Infractions ({infractions.length})</>}>
          <Table variant="dark" hover responsive style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>#</th>
                <th>Véhicule</th>
                <th>Type</th>
                <th>Vitesse</th>
                <th>Limite</th>
                <th>Excès</th>
                <th>Amende</th>
                <th>Sévérité</th>
                <th>Statut</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {infractions.map((inf) => {
                const excess = inf.recorded_speed && inf.speed_limit ? Math.round(inf.recorded_speed - inf.speed_limit) : 0;
                const sev = SEVERITY_CFG[inf.infraction_type_code ? types.find(t => t.id === inf.infraction_type)?.severity : 'moderate'] || SEVERITY_CFG.moderate;
                const st = STATUS_CFG[inf.status] || STATUS_CFG.pending;
                return (
                  <tr key={inf.id}>
                    <td>{inf.id}</td>
                    <td><strong>{inf.vehicle_plate || '-'}</strong></td>
                    <td>
                      <span style={{ color: '#ff9100', fontWeight: 600 }}>{inf.infraction_type_code}</span>
                      <br /><span style={{ color: '#888', fontSize: 11 }}>{inf.infraction_type_name}</span>
                    </td>
                    <td style={{ color: '#ff1744', fontWeight: 700 }}>
                      {inf.recorded_speed ? `${Math.round(inf.recorded_speed)} km/h` : '-'}
                    </td>
                    <td>{inf.speed_limit ? `${inf.speed_limit} km/h` : '-'}</td>
                    <td style={{ color: excess > 0 ? '#ff9100' : '#888', fontWeight: 700 }}>
                      {excess > 0 ? `+${excess} km/h` : '-'}
                    </td>
                    <td style={{ color: '#ffea00' }}>{parseFloat(inf.fine_amount).toLocaleString()} FC</td>
                    <td><Badge bg={sev.bg}>{sev.label}</Badge></td>
                    <td><Badge bg={st.bg}>{st.label}</Badge></td>
                    <td style={{ fontSize: 11 }}>{new Date(inf.created_at).toLocaleString('fr-FR')}</td>
                    <td>
                      <div className="d-flex gap-1">
                        {inf.status === 'pending' && (
                          <>
                            <Button size="sm" variant="info" className="py-0" onClick={() => handleInfractionAction(inf.id, 'confirm')}>
                              <i className="bi bi-check"></i>
                            </Button>
                            <Button size="sm" variant="danger" className="py-0" onClick={() => handleInfractionAction(inf.id, 'sanction')}>
                              <i className="bi bi-currency-dollar"></i>
                            </Button>
                            <Button size="sm" variant="secondary" className="py-0" onClick={() => handleInfractionAction(inf.id, 'dismiss')}>
                              <i className="bi bi-x"></i>
                            </Button>
                          </>
                        )}
                        {inf.status === 'confirmed' && (
                          <Button size="sm" variant="danger" className="py-0" onClick={() => handleInfractionAction(inf.id, 'sanction')}>
                            <i className="bi bi-currency-dollar"></i> Sanctionner
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {infractions.length === 0 && (
                <tr><td colSpan={11} className="text-center" style={{ color: '#555' }}>Aucune infraction enregistrée</td></tr>
              )}
            </tbody>
          </Table>
        </Tab>

        {/* ═══════ TAB: RULES (segments) ═══════ */}
        <Tab eventKey="rules" title={<><i className="bi bi-signpost-2 me-1"></i>Règles ({rules.length})</>}>
          <Button variant="outline-warning" size="sm" className="mb-3" onClick={() => openRuleModal()}>
            <i className="bi bi-plus-circle me-1"></i> Nouvelle règle
          </Button>

          {/* Map showing all rule segments */}
          <div style={{ height: 300, borderRadius: 12, overflow: 'hidden', marginBottom: 16, border: '1px solid #333' }}>
            <MapContainer center={[-4.325, 15.322]} zoom={12} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {rules.map((r) => (
                <React.Fragment key={r.id}>
                  <Polyline
                    positions={[[r.start_lat, r.start_lng], [r.end_lat, r.end_lng]]}
                    color={r.active ? '#ff9100' : '#555'}
                    weight={4}
                    dashArray={r.active ? undefined : '8 4'}
                  />
                  <Marker position={[r.start_lat, r.start_lng]} icon={startIcon}>
                    <Popup><strong>A:</strong> {r.name}</Popup>
                  </Marker>
                  <Marker position={[r.end_lat, r.end_lng]} icon={endIcon}>
                    <Popup><strong>B:</strong> {r.name}{r.speed_limit ? ` — max ${r.speed_limit} km/h` : ''}</Popup>
                  </Marker>
                </React.Fragment>
              ))}
            </MapContainer>
          </div>

          <Table variant="dark" hover responsive style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Type</th>
                <th>Limite</th>
                <th>Corridor</th>
                <th>Horaires</th>
                <th>Actif</th>
                <th>Infractions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.name}</strong></td>
                  <td style={{ color: '#ff9100' }}>{r.infraction_type_name} <span style={{ color: '#888' }}>({r.infraction_type_code})</span></td>
                  <td>{r.speed_limit ? `${r.speed_limit} km/h` : '-'}</td>
                  <td>{r.corridor_m}m</td>
                  <td style={{ fontSize: 11, color: '#888' }}>
                    {r.active_start && r.active_end ? `${r.active_start}–${r.active_end}` : '24h'}
                    {r.active_days?.length > 0 && <><br />{r.active_days.map(d => DAYS[d]).join(', ')}</>}
                  </td>
                  <td><Badge bg={r.active ? 'success' : 'secondary'}>{r.active ? 'Oui' : 'Non'}</Badge></td>
                  <td>{infractions.filter(i => i.rule === r.id).length}</td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button size="sm" variant="outline-light" className="py-0" onClick={() => openRuleModal(r)}>
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button size="sm" variant="outline-danger" className="py-0" onClick={() => deleteRule(r.id)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr><td colSpan={8} className="text-center" style={{ color: '#555' }}>Aucune règle configurée</td></tr>
              )}
            </tbody>
          </Table>
        </Tab>

        {/* ═══════ TAB: TYPES ═══════ */}
        <Tab eventKey="types" title={<><i className="bi bi-tags me-1"></i>Types ({types.length})</>}>
          <Button variant="outline-info" size="sm" className="mb-3" onClick={() => openTypeModal()}>
            <i className="bi bi-plus-circle me-1"></i> Nouveau type
          </Button>
          <Table variant="dark" hover responsive style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Nom</th>
                <th>Sévérité</th>
                <th>Amende (FC)</th>
                <th>Points</th>
                <th>Actif</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => {
                const sev = SEVERITY_CFG[t.severity] || SEVERITY_CFG.moderate;
                return (
                  <tr key={t.id}>
                    <td style={{ color: '#ff9100', fontWeight: 700 }}>{t.code}</td>
                    <td><strong>{t.name}</strong>{t.description && <><br /><span style={{ color: '#888', fontSize: 11 }}>{t.description}</span></>}</td>
                    <td><Badge bg={sev.bg}>{sev.label}</Badge></td>
                    <td style={{ color: '#ffea00' }}>{parseFloat(t.fine_amount).toLocaleString()} FC</td>
                    <td>{t.points_penalty}</td>
                    <td><Badge bg={t.active ? 'success' : 'secondary'}>{t.active ? 'Oui' : 'Non'}</Badge></td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button size="sm" variant="outline-light" className="py-0" onClick={() => openTypeModal(t)}>
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button size="sm" variant="outline-danger" className="py-0" onClick={() => deleteType(t.id)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {types.length === 0 && (
                <tr><td colSpan={7} className="text-center" style={{ color: '#555' }}>Aucun type d'infraction</td></tr>
              )}
            </tbody>
          </Table>
        </Tab>
      </Tabs>

      {/* ════ TYPE MODAL ════ */}
      <Modal show={showTypeModal} onHide={() => setShowTypeModal(false)} centered>
        <Modal.Header closeButton style={{ background: '#1a1a2e', borderColor: '#333' }}>
          <Modal.Title style={{ color: '#ff9100', fontSize: 16 }}>
            {editType ? 'Modifier' : 'Nouveau'} type d'infraction
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#0d0d1a' }}>
          <Form.Group className="mb-3">
            <Form.Label className="text-light" style={{ fontSize: 13 }}>Code</Form.Label>
            <Form.Control size="sm" value={typeForm.code} onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value.toUpperCase() })}
              placeholder="EXC_VIT" style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="text-light" style={{ fontSize: 13 }}>Nom</Form.Label>
            <Form.Control size="sm" value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
              placeholder="Excès de vitesse" style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="text-light" style={{ fontSize: 13 }}>Description</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={typeForm.description}
              onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
              style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
          </Form.Group>
          <div className="d-flex gap-2 mb-3">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="text-light" style={{ fontSize: 13 }}>Sévérité</Form.Label>
              <Form.Select size="sm" value={typeForm.severity} onChange={(e) => setTypeForm({ ...typeForm, severity: e.target.value })}
                style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }}>
                <option value="minor">Mineure</option>
                <option value="moderate">Modérée</option>
                <option value="serious">Grave</option>
                <option value="critical">Critique</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="text-light" style={{ fontSize: 13 }}>Amende (FC)</Form.Label>
              <Form.Control size="sm" type="number" value={typeForm.fine_amount}
                onChange={(e) => setTypeForm({ ...typeForm, fine_amount: e.target.value })}
                style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="text-light" style={{ fontSize: 13 }}>Points</Form.Label>
              <Form.Control size="sm" type="number" value={typeForm.points_penalty}
                onChange={(e) => setTypeForm({ ...typeForm, points_penalty: e.target.value })}
                style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: '#1a1a2e', borderColor: '#333' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowTypeModal(false)}>Annuler</Button>
          <Button variant="warning" size="sm" onClick={saveType}>
            <i className="bi bi-check-circle me-1"></i> {editType ? 'Modifier' : 'Créer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ════ RULE MODAL ════ */}
      <Modal show={showRuleModal} onHide={() => setShowRuleModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: '#1a1a2e', borderColor: '#333' }}>
          <Modal.Title style={{ color: '#ff9100', fontSize: 16 }}>
            {editRule ? 'Modifier' : 'Nouvelle'} règle d'infraction
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#0d0d1a' }}>
          <div className="d-flex gap-3 mb-3">
            <Form.Group style={{ flex: 2 }}>
              <Form.Label className="text-light" style={{ fontSize: 13 }}>Nom du tronçon</Form.Label>
              <Form.Control size="sm" value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                placeholder="Boulevard du 30 Juin — section Gare" style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="text-light" style={{ fontSize: 13 }}>Type d'infraction</Form.Label>
              <Form.Select size="sm" value={ruleForm.infraction_type}
                onChange={(e) => setRuleForm({ ...ruleForm, infraction_type: parseInt(e.target.value) || '' })}
                style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }}>
                <option value="">Choisir...</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
              </Form.Select>
            </Form.Group>
          </div>

          {/* Map for segment picking */}
          <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
            Cliquez sur la carte pour sélectionner les points A et B du segment routier.
          </div>
          <div style={{ height: 450, borderRadius: 10, overflow: 'hidden', marginBottom: 12, border: `2px solid ${pickingPoint ? '#00e676' : '#333'}` }}>
            <MapContainer center={[-4.325, 15.322]} zoom={13} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapClickHandler onPick={handleMapPick} />
              {ruleForm.start_lat && ruleForm.start_lng && (
                <Marker position={[parseFloat(ruleForm.start_lat), parseFloat(ruleForm.start_lng)]} icon={startIcon}>
                  <Popup>Point A (départ)</Popup>
                </Marker>
              )}
              {ruleForm.end_lat && ruleForm.end_lng && (
                <Marker position={[parseFloat(ruleForm.end_lat), parseFloat(ruleForm.end_lng)]} icon={endIcon}>
                  <Popup>Point B (fin)</Popup>
                </Marker>
              )}
              {ruleForm.start_lat && ruleForm.end_lat && (
                <Polyline
                  positions={[
                    [parseFloat(ruleForm.start_lat), parseFloat(ruleForm.start_lng)],
                    [parseFloat(ruleForm.end_lat), parseFloat(ruleForm.end_lng)],
                  ]}
                  color="#ff9100" weight={4}
                />
              )}
            </MapContainer>
          </div>

          <div className="d-flex gap-2 mb-3">
            <Button size="sm" variant={pickingPoint === 'start' ? 'success' : 'outline-success'}
              onClick={() => setPickingPoint(pickingPoint === 'start' ? null : 'start')}>
              <i className="bi bi-geo-alt me-1"></i> Point A {ruleForm.start_lat ? `(${parseFloat(ruleForm.start_lat).toFixed(4)}, ${parseFloat(ruleForm.start_lng).toFixed(4)})` : ''}
            </Button>
            <Button size="sm" variant={pickingPoint === 'end' ? 'danger' : 'outline-danger'}
              onClick={() => setPickingPoint(pickingPoint === 'end' ? null : 'end')}>
              <i className="bi bi-geo-alt-fill me-1"></i> Point B {ruleForm.end_lat ? `(${parseFloat(ruleForm.end_lat).toFixed(4)}, ${parseFloat(ruleForm.end_lng).toFixed(4)})` : ''}
            </Button>
          </div>

          <div className="d-flex gap-2 mb-3">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="text-light" style={{ fontSize: 13 }}>Limite vitesse (km/h)</Form.Label>
              <Form.Control size="sm" type="number" value={ruleForm.speed_limit}
                onChange={(e) => setRuleForm({ ...ruleForm, speed_limit: e.target.value })}
                placeholder="80" style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="text-light" style={{ fontSize: 13 }}>Corridor (m)</Form.Label>
              <Form.Control size="sm" type="number" value={ruleForm.corridor_m}
                onChange={(e) => setRuleForm({ ...ruleForm, corridor_m: parseInt(e.target.value) || 50 })}
                style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
            </Form.Group>
          </div>

          <div className="d-flex gap-2 mb-3">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="text-light" style={{ fontSize: 13 }}>Heure début</Form.Label>
              <Form.Control size="sm" type="time" value={ruleForm.active_start}
                onChange={(e) => setRuleForm({ ...ruleForm, active_start: e.target.value })}
                style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label className="text-light" style={{ fontSize: 13 }}>Heure fin</Form.Label>
              <Form.Control size="sm" type="time" value={ruleForm.active_end}
                onChange={(e) => setRuleForm({ ...ruleForm, active_end: e.target.value })}
                style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }} />
            </Form.Group>
          </div>

          <Form.Group className="mb-2">
            <Form.Label className="text-light" style={{ fontSize: 13 }}>Jours actifs</Form.Label>
            <div className="d-flex gap-2">
              {DAYS.map((d, i) => (
                <Button key={i} size="sm"
                  variant={ruleForm.active_days.includes(i) ? 'warning' : 'outline-secondary'}
                  onClick={() => {
                    setRuleForm((f) => ({
                      ...f,
                      active_days: f.active_days.includes(i)
                        ? f.active_days.filter((x) => x !== i)
                        : [...f.active_days, i],
                    }));
                  }}
                  style={{ minWidth: 42 }}
                >
                  {d}
                </Button>
              ))}
            </div>
            <Form.Text className="text-muted" style={{ fontSize: 11 }}>Vide = tous les jours</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ background: '#1a1a2e', borderColor: '#333' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowRuleModal(false)}>Annuler</Button>
          <Button variant="warning" size="sm" onClick={saveRule}>
            <i className="bi bi-check-circle me-1"></i> {editRule ? 'Modifier' : 'Créer'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
